import { filterObject } from '../helper'
import isequal from 'lodash.isequal'
import ExtendableError from '../../../../error'

const oneDayBase = '((?:Mon|Tue|Wed|Thu|Fri|Sat|Sun|Holidays)(?:,(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun|Holidays)){0,7}|Mon-Sun|Mon-Fri)'
  + ' ('
  + '(?:(?:[01]\\d|2[0-3]):[0-5]\\d-(?:(?:[01]\\d|2[0-3]):[0-5]\\d|24:00), ?)*'
  + '(?:[01]\\d|2[0-3]):[0-5]\\d-(?:(?:[01]\\d|2[0-3]):[0-5]\\d|24:00)'
  + '|-)'
const oneDayRegex = '^' + oneDayBase + '$'
const allDaysRegex = '^' + oneDayBase + '(?:\\n' + oneDayBase + '){0,7}$'
const hoursRegex = '((?:[01]\\d|2[0-3]):[0-5]\\d)-((?:[01]\\d|2[0-3]):[0-5]\\d|24:00)'
const oneDayRegExp = new RegExp(oneDayRegex)
const allDaysRegExp = new RegExp(allDaysRegex)
const hoursRegExp = new RegExp(hoursRegex, 'g')

class HoursValidationError extends ExtendableError {
  constructor (message, errorCode) {
    super(message)
    this.errorCode = errorCode
  }
}

class Hours {
  static DAY_EXTEND_MAP = Object.freeze({
    'Mon-Sun': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    'Mon-Fri': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  })

  constructor (value) {
    this._hours = {
      'Mon': [],
      'Tue': [],
      'Wed': [],
      'Thu': [],
      'Fri': [],
      'Sat': [],
      'Sun': [],
      'Holidays': []
    }
    this.parseValue(value)
  }

  toString () {
    return this.format(filterObject(this._hours, (times) => {
      return times.length > 0
    }), ', ')
  }

  getConsolidated () {
    const hours = this._hours
    const out = {}
    const dayNames = Object.keys(hours)
    for (let i = 0; i < 8; ++i) {
      if (!hours.hasOwnProperty(dayNames[i])) {
        continue
      }
      if (hours[dayNames[i]].length === 0) {
        delete hours[dayNames[i]]
        continue
      }
      const days = [dayNames[i]]
      const v = hours[dayNames[i]]
      for (let j = i + 1; j < 7; ++j) {
        if (!hours.hasOwnProperty(dayNames[j]) || !isequal(v, hours[dayNames[j]])) {
          continue
        }
        days.push(dayNames[j])
        delete hours[dayNames[j]]
      }

      let foundIndex = null
      Object.keys(Hours.DAY_EXTEND_MAP).every((key) => {
        if (isequal(Hours.DAY_EXTEND_MAP[key], days)) {
          out[key] = v
          foundIndex = key
          return false
        }
        return true
      })
      if (foundIndex === null) {
        out[days.join(',')] = v
      }
    }
    return this.format(filterObject(out, times => {
      return times.length > 0
    }), ',')

  }

  parseValue (value) {
    if (value === '') {
      return
    }
    if (allDaysRegExp.test(value) === false) {
      throw new HoursValidationError('Cannot parse hours', 'ERROR_FACT_ATTR_VALUE_HOURS_NOT')
    }
    const lines = value.split('\n')
    for (let i = 0; i < lines.length; i++) {
      oneDayRegExp.lastIndex = 0
      const matches = oneDayRegExp.exec(lines[i])
      if (matches[2] === '-') {
        continue
      }
      let h
      hoursRegExp.lastIndex = 0
      while (h = hoursRegExp.exec(matches[2])) {// eslint-disable-line
        this.addHours(matches[1], h[1], h[2] === '00:00' ? '24:00' : h[2])
      }
    }
    // sort each day by opening time
    Object.values(this._hours).forEach((times) => {
      times.sort((a, b) => a[0] < b[0] ? -1 : 1)
    })
  }

  addHours (dayString, start, end) {
    if (!(start < end)) {
      throw new HoursValidationError('Starting time ' + start + ' is greater than ending time ' + end, 'ERROR_FACT_ATTR_VALUE_HOURS_START_END_MISMATCH')
    }
    const days = Hours.DAY_EXTEND_MAP[dayString] ?? dayString.split(',')
    day_loop:
      for (let i = 0; i < days.length; i++) {
        for (let j = 0; j < this._hours[days[i]].length; j++) {
          let t = this._hours[days[i]][j]
          if (start === t[0] && end === t[1]) {
            continue day_loop
          }
          if (end >= t[0] && start <= t[1]) {
            throw new HoursValidationError(
              'Time intervals on ' + days[i] + ' intersect (' + start + '-' + end + '/' + t[0] + '-' + t[1] + ')',
              'ERROR_FACT_ATTR_VALUE_HOURS_START_END_INTERSECT'
            )
          }
        }
        this._hours[days[i]].push([start, end])
      }
  }

  getHoursMapByDay () {
    return this._hours
  }

  format (times, hoursSeparator = ',') {
    const fn = function (dayStr, times) {
      return dayStr + ' ' + times.map((time) => time[0] + '-' + time[1]).join(hoursSeparator)
    }
    let ret = []
    Object.entries(times).forEach((v) => {
      ret.push(fn(v[0], v[1]))
    })
    return ret.join('\n')
  }
}

export {
  Hours,
  HoursValidationError,
}
